import InvoiceCode from "./InvoiceCode.js";
import Model from "../../Model.js";

/**
 * `invoice` is a class representing what someone owes
 * to soweone else.
 *
 * @param {number} spec.id - Unique identifier of the invoice
 * @param {number} spec.total - Total value of the invoices
 * @param {number} spec.convertedTotal - Total value of the invoices after conversion to base currency
 * @param {number} spec.balance - Total value of what has already been paid
 * @param {number} spec.convertedBalance - Total value of what has already been paid after conversion to base currency
 * @param {Date} spec.dueDate - Date at which the invoice should be paid
 * @param {number} spec.dueDateWeekNumber - Week number of spec.dueDate
 * @param {Date} spec.invoiceDate - Date at which the invoice got created
 * @param {string} spec.partyName - Name of the customer to which the invoice is addressed or name of the supplier who generated the invoice
 * @param {{}} spec.partyCode - Identifier of the customer to which the invoice is addressed or identifier of the supplier who generated the invoice
 * @param {InvoiceCode} spec.invoiceCode - Identifier of the invoice
 * @param {string} spec.currencyCode - 3-letter code for the currency of the invoice
 * @param {number} spec.currencyRate - Conversion rate to base currency
 */
export default class Invoice extends Model {
	constructor({
		id,
		total,
		convertedTotal,
		balance,
		convertedBalance,
		dueDate,
		dueDateWeekNumber,
		invoiceDate,
		partyName,
		partyCode,
		invoiceCode, // only for payables
		currencyCode,
		currencyRate,
	}) {
		super(...arguments);
		this._id = id;
		this._total = total;
		this._convertedTotal = convertedTotal;
		this._balance = balance;
		this._convertedBalance = convertedBalance;
		this._dueDate = dueDate;
		this._dueDateWeekNumber = dueDateWeekNumber;

		this._invoiceDate = invoiceDate;
		this._partyName = partyName;
		this._partyCode = partyCode;
		this._invoiceCode = invoiceCode;
		this._currencyCode = currencyCode;
		this._currencyRate = currencyRate;

		// Save in unstructuredFields everything we receive from
		// the server this we don't manage as an explicite
		// field. this's currently useful to better understand
		// what fields coming from Xero and Fortnox we need to
		// display.
		this._unstructuredFields = [...arguments][0];

		[
			"id",
			"total",
			"convertedTotal",
			"balance",
			"convertedBalance",
			"dueDate",
			"dueDateWeekNumber",
			"invoiceDate",
			"partyName",
			"partyCode",
			"invoiceCode",
			"ocr",
			"currencyCode",
			"currencyRate",
		].forEach((key) => delete this._unstructuredFields[key]);
	}

	getId() {
		return this._id;
	}

	getBalance() {
		return this._balance;
	}

	getConvertedBalance() {
		return this._convertedBalance;
	}

	getTotal() {
		return this._total;
	}

	getConvertedTotal() {
		return this._convertedTotal;
	}

	getDueDate() {
		return this._dueDate;
	}

	getDueDateWeekNumber() {
		return this._dueDateWeekNumber;
	}

	getInvoiceDate() {
		return this._invoiceDate;
	}

	getPartyName() {
		return this._partyName;
	}

	getPartyCode() {
		return this._partyCode;
	}

	getInvoiceCode() {
		return this._invoiceCode;
	}

	getCurrencyCode() {
		return this._currencyCode;
	}

	getInvoiceCodeLabel() {
		return this._invoiceCode.getLabel();
	}

	getUnstructuredFields() {
		return this._unstructuredFields;
	}

	static transformMaterializationData(data) {
		data.dueDate &&= new Date(data.dueDate);

		data.invoiceDate = new Date(data.invoiceDate);

		data.invoiceCode = new InvoiceCode({
			ocr: data.ocr,
			code: data.invoiceCode,
		});
	}
}
